import { useState } from "react";
import { useData } from "./context/DataContext";
import Dashboard from "./components/Dashboard";
import Lehrpersonen from "./components/Lehrpersonen";
import Klassen from "./components/Klassen";
import Faecher from "./components/Faecher";
import FachDetail from "./components/FachDetail";
import Zuteilung from "./components/Zuteilung";
import GekoppelteKlassen from "./components/GekoppelteKlassen";
import Reports from "./components/Reports";
import Wiki from "./components/Wiki";
import { exportToExcel, importFromExcel } from "./utils/excelExport";
import { TabName } from "./types";
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  ClipboardList,
  Link2,
  Menu,
  X,
  School,
  BarChart3,
  HelpCircle,
  Database,
  Trash2,
  Download,
  Upload,
} from "lucide-react";
import { cn } from "./utils/cn";

function App() {
  const { activeTab, setActiveTab, data, importData, loadDemoData, clearAllData } = useData();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const renderTabContent = () => {
    switch (activeTab) {
      case "dashboard":
        return <Dashboard />;
      case "lehrpersonen":
        return <Lehrpersonen />;
      case "klassen":
        return <Klassen />;
      case "faecher":
        return <Faecher />;
      case "fachDetail":
        return <FachDetail />;
      case "zuteilung":
        return <Zuteilung />;
      case "gekoppelt":
        return <GekoppelteKlassen />;
      case "reports":
        return <Reports />;
case "wiki":
        return <Wiki />;
      default:
        return <Dashboard />;
    }
  };

  const navigationItems: {
    name: TabName;
    label: string;
    icon: React.ElementType;
  }[] = [
    { name: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { name: "lehrpersonen", label: "Lehrpersonen", icon: Users },
    { name: "klassen", label: "Klassen", icon: GraduationCap },
    { name: "faecher", label: "Fächer", icon: BookOpen },
    { name: "zuteilung", label: "Stundenzuteilung", icon: ClipboardList },
    { name: "gekoppelt", label: "Gekoppelte Klassen", icon: Link2 },
    { name: "reports", label: "Berichte & Analysen", icon: BarChart3 },
    { name: "wiki", label: "Hilfe & Doku", icon: HelpCircle },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-40 h-screen transition-all duration-300 ease-in-out",
          "bg-white border-r border-gray-200 shadow-sm overflow-hidden",
          sidebarOpen ? "w-64" : "w-16"
        )}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Logo Header */}
          <div
            className={cn(
              "flex items-center border-b border-gray-200 h-[73px]",
              sidebarOpen ? "justify-between px-6" : "justify-center px-4"
            )}
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary-600 rounded-lg flex-shrink-0">
                <School className="w-6 h-6 text-white" />
              </div>
              {sidebarOpen && (
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-gray-900">
                    Adjunkty
                  </span>
                  <span className="text-xs text-gray-500">Schulplanung</span>
                </div>
              )}
            </div>
            {sidebarOpen && (
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            )}
          </div>

          {/* Navigation */}
          <nav
            className={cn(
              "flex-1 py-6 space-y-1 overflow-y-auto",
              sidebarOpen ? "px-3" : "px-2"
            )}
          >
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.name;
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    setActiveTab(item.name);
                    if (window.innerWidth < 1024) setSidebarOpen(false);
                  }}
                  title={!sidebarOpen ? item.label : undefined}
                  className={cn(
                    "w-full flex items-center rounded-lg font-medium transition-all duration-200",
                    sidebarOpen ? "space-x-3 px-4 py-3" : "justify-center p-3",
                    isActive
                      ? "bg-primary-50 text-primary-700 shadow-sm"
                      : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                  )}
                >
                  <Icon
                    className={cn(
                      "w-5 h-5 flex-shrink-0",
                      isActive && "text-primary-600"
                    )}
                  />
                  {sidebarOpen && <span className="text-sm">{item.label}</span>}
                </button>
              );
            })}
          </nav>

          {/* Footer */}
          {sidebarOpen && (
          <div className="p-4 border-t border-gray-200 space-y-2">
            <div className="flex gap-2">
              <button
                onClick={loadDemoData}
                className="flex-1 flex items-center justify-center gap-2 px-3 h-10 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
              >
                <Database className="w-3.5 h-3.5" />
                Demo laden
              </button>
              <button
                onClick={() => {
                  if (confirm("Alle Daten löschen?")) {
                    clearAllData();
                  }
                }}
                className="flex-1 flex items-center justify-center gap-2 px-3 h-10 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Löschen
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => exportToExcel(data)}
                className="flex-1 flex items-center justify-center gap-2 px-3 h-10 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Export
              </button>
              <label className="flex-1 flex items-center justify-center gap-2 px-3 h-10 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors cursor-pointer">
                <Upload className="w-3.5 h-3.5" />
                Import
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const imported = await importFromExcel(file);
                      importData(imported);
                    } catch (err) {
                      alert(`Import fehlgeschlagen: ${err instanceof Error ? err.message : err}`);
                    }
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
          </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={cn(
          "transition-all duration-300 ease-in-out",
          sidebarOpen ? "ml-64" : "ml-16"
        )}
      >
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm h-[73px]">
          <div className="flex items-center justify-between px-4 lg:px-8 h-full">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Menu className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {navigationItems.find((item) => item.name === activeTab)
                    ?.label || "Dashboard"}
                </h1>
                <p className="text-sm text-gray-500">
                  Digitale Stundenzuteilung für Gymnasien
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="p-4 lg:p-8">
          <div className="animate-fade-in">{renderTabContent()}</div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}

export default App;
